import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import type { ObjectView } from '../lib/api.js';

/** Kleiner runder Emoji-Pin (kompakter als in der großen MapCard). */
function pin(color: string, label: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 3px rgba(0,0,0,0.5);border:1.5px solid #fff;font-size:11px;line-height:1;">${label}</div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

const OBJECT_COLOR = '#7c3aed';
const PRIMARY_COLOR = '#2563eb';
const SECONDARY_COLOR = '#0ea5e9';

/**
 * Kompakte, NICHT-interaktive Standort-Karte für die Swipe-Karte: Objekt-Pin +
 * Anker-Pins + gestrichelte Linien — dieselben Standorte wie in der großen MapCard,
 * nur klein und als reine Vorschau. `pointer-events:none` → Wischgesten gehen an die
 * Swipe-Karte durch (die Mini-Karte selbst fängt keine Touches ab).
 */
export function MiniMap({ object, height }: { object: ObjectView; height: string }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (object.lat === null || object.lng === null) return;
    const objLatLng: [number, number] = [object.lat, object.lng];

    const map = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);

    const points: Array<[number, number]> = [objLatLng];
    const objEmoji = object.assignedProfile === 'B_buero' ? '🏢' : '🏠';
    L.marker(objLatLng, { icon: pin(OBJECT_COLOR, objEmoji) }).addTo(map);

    for (const c of object.commutes) {
      const ll: [number, number] = [c.lat, c.lng];
      points.push(ll);
      const color = c.role === 'primary' ? PRIMARY_COLOR : SECONDARY_COLOR;
      L.marker(ll, { icon: pin(color, c.icon ?? '📍') }).addTo(map);
      L.polyline([objLatLng, ll], { color, weight: 2, opacity: 0.5, dashArray: '3 5' }).addTo(map);
    }

    map.fitBounds(L.latLngBounds(points), { padding: [22, 22], maxZoom: 13 });
    const t = setTimeout(() => map.invalidateSize(), 120);

    return () => {
      clearTimeout(t);
      map.remove();
    };
  }, [object]);

  return (
    <div
      ref={containerRef}
      // position+zIndex:0 = eigener Stacking-Context → Leaflets interne Panes (z-index bis
      // 700) bleiben gekapselt und überlagern nicht die Swipe-Overlays/Badges der Karte.
      style={{
        height,
        width: '100%',
        pointerEvents: 'none',
        background: '#e5e7eb',
        position: 'relative',
        zIndex: 0,
      }}
    />
  );
}
